import hyperdom from 'hyperdom'
import PartyPicker from './partyPicker.jsx'
import RoomOptions from '../../../roomOptions.jsx'
import format from 'root-core/utils/text/stringFormatFilter.js'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import responsiveRender from '../../../responsiveRender.js'
import DateOfBirthPicker from '../../../components/dateOfBirthPicker.jsx'
import ValidationBase from '../../validationBase.jsx'
import TourOverviewPassengerSchema from './tourOverviewPassengerSchema.js'
import generateDOB from '../../../utils/generateDOB.js'
import {Message, MessageTypes} from '../../../message.jsx'

export default class TourOverviewTab extends ValidationBase {
  constructor ({
    user,
    config,
    departure,
    quoteCriteria,
    modals,
    flights,
    promisedFlights,
    extraStays,
    nextTab,
    progress,
  }) {
    super()
    this.user = user
    this.flights = flights
    this.promisedFlights = promisedFlights
    this.extraStays = extraStays
    this.quoteCriteria = quoteCriteria
    this.progress = progress
    this.roomOptions = new RoomOptions({
      binding: {
        get: () => quoteCriteria.selectedRoomAllocation,
        set: (rooms) => {
          if(quoteCriteria.selectedRoomAllocation !== rooms) {
            this.extraStays.showHotelsRemovedWarning = false
            quoteCriteria.selectRoomAllocation(rooms)
          }
        },
      },
      config,
      quoteCriteria,
      currency: departure.prices.currency,
    })

    this.nextTab = nextTab
    this.departure = departure
    this.modals = modals
    this.config = config
    this.isDobUpdated = false

    this.partyPicker = this.setupPartyPicker()

    this.schema = new TourOverviewPassengerSchema({
      config: this.config,
      departure: this.departure,
    }).buildSchema()
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  validateParty () {
    return this.partyPicker.validateParty(this.quoteCriteria.party)
  }

  validate () {
    return super.validate().valid && !this.quoteCriteria.noRoomsAvailable
  }

  setupPartyPicker () {
    return new PartyPicker({
      quoteCriteria: this.quoteCriteria,
      modals: this.modals,
      childrenAllowed: this.departure.childrenAllowed,
      config: this.config,
      resetExtraStays: this.extraStays.resetExtraStays.bind(this.extraStays),
      user: this.user,
      departure: this.departure,
    })
  }

  showRoomOnRequestModal () {
    const {roomsAvailability, brandPages, brandName} = this.config
    this.modals.show({
      backdropCloseDisabled: true,
      classNames: 'o-room-on-request-modal',
      dataTest: 'room-on-request-modal',
      render: () => this.renderWarningModal({
        title: "This option is on request",
        showChangeOptionsButton: true,
        text: roomsAvailability.roomOnRequest,
        brandWebsite: brandPages.brandWebsite,
        brandName,
      }),
    })
  }

  showTripOnRequestModal () {
    const {roomsAvailability, brandPages, brandName} = this.config
    this.modals.show({
      backdropCloseDisabled: true,
      classNames: 'o-trip-on-request-modal',
      dataTest: 'trip-on-request-modal',
      render: () => this.renderWarningModal({
        title: "Limited Availability",
        showChangeOptionsButton: false,
        text: roomsAvailability.tripOnRequest,
        brandWebsite: brandPages.brandWebsite,
        brandName,
      }),
    })
  }

  renderWarningModal ({title, showChangeOptionsButton, text, brandWebsite, brandName}) {
    return [
      <div class="modal__header">
        <div>{title}</div>
      </div>,
      hyperdom.rawHtml('p', format(text, brandWebsite, brandName)),
      <div class="action-buttons">
        {showChangeOptionsButton
          ? <div class="action-button-left">
            <button data-test="on-request-modal-change" class="btn-secondary-borderless -tour-overview-change-options-continue" onclick={() => {this.modals.close()}}>Change options</button>
          </div>
          : undefined
        }
        <div class="action-button-right">
          <button data-test="on-request-modal-continue"  class="btn-secondary -tour-overview-on-request-continue" onclick={() => {this.nextTab(); return this.modals.close()}}>Continue</button>
        </div>
      </div>,
    ]
  }

  renderRoomInfoMessage () {
    return <div>
      <h3 className="tour-overview__title">Room Type</h3>
      <div className="message-info">
        <h2>Tip!</h2>
        <p>Prices shown below are for the room options only. Discounts are applied to the land portion of the trip price
          (excluding single room surcharges) and can be seen in the full cost breakdown.</p>
      </div>
    </div>
  }
  renderClosedRoomWarning () {
    return <div id='message-warning'>
      {
        new Message({
          header: 'Sorry!',
          message: format(this.config.errorMessages.noRoomsAvailable, this.config.phone.general.split(';').shift()),
          type: MessageTypes.WARNING,
        })
      }
    </div>
  }
  renderRoomOptions () {
    return this.progress.criteriaLoaded
      ? this.roomOptions
      : undefined
  }
  renderPartyPicker () {
    return this.user.loaded ? this.partyPicker : undefined
  }

  renderPartyPickerTitle () {
    return <div className="tour-overview__title-box">
      <h3 className="tour-overview__title">Passengers</h3>
      <div className="tooltip party-picker__help-tip">
        <div className="tooltip__content">
          <h4 className="tooltip__header">Please be aware that you can book a maximum of 8 people online.</h4>
          <p>We welcome larger groups – if you would like to book for more than 8 people, please
            {this.config.phone.general.startsWith('http')
              ? <span> contact us via <a href={this.config.phone.general}>{this.config.phone.general}</a></span>
              : <span> call us on <a href={'tel:' + this.config.phone.general}>{this.config.phone.general}</a></span>
            } and one of the team will help you with your booking</p>
        </div>
      </div>
    </div>
  }
  renderChildDatePicker () {
    return this.quoteCriteria.party.children ? this.renderChildDatePickers() : null
  }

  render () {
    const isRoomClosed = this.roomOptions.binding.get()?.status === 'closed'
    return <div>
      <div class="tour-overview u-clearfix" data-test="tour-overview-tab">
        {this.renderPartyPickerTitle()}
        {this.renderPartyPicker()}
        {this.renderChildDatePicker()}
        {this.flights.renderFlightsRemovedForPartyWarning()}
        {this.extraStays.renderHotelsRemovedWarning()}
        {this.renderRoomInfoMessage()}
        {isRoomClosed ? this.renderClosedRoomWarning() : ''}
        {this.renderRoomOptions()}
        {this.renderAdditionalCTA()}
      </div>
    </div>
  }

  renderChildDatePickers() {
    const children = this.quoteCriteria.passengers.filter(
      (passenger) => passenger.type === "child"
    )
    const datePickers = children.map((child, index) => {
      return (
        <div className={`tour-overview-dob-picker form-date-of-birth-field-${index}`}>
          <span className="tour-overview-dob-picker__label">
            Child {index + 1}:
          </span>
          <div className="tour-overview-dob-picker__field">
            <div>
              {
                new DateOfBirthPicker({
                  placeholder: `Date of birth*`,
                  binding: {
                    get: () => child.dateOfBirth,
                    set: (value) => (child.dateOfBirth = value),
                    fieldVisited: () => {
                      this.partyPicker.partyChanged()
                      this.fieldVisited(
                        child,
                        "dateOfBirth",
                        `pax-date-of-birth-to-${index}`
                      )
                    },
                    setIsDobUpdated: (value) => (this.isDobUpdated = value),
                    isDobUpdated: () => this.isDobUpdated,
                  },
                  isFieldInvalid: () => this.isFieldInvalid(child, "dateOfBirth"),
                  config: this.config,
                  isDisabled: false,
                })
              }
            </div>
            {this.renderValidationError(child, "dateOfBirth")}
          </div>
        </div>
      )
    })

    const fillInFakeDataButton = this.config.features.devLinks && (
      <a
        role="button"
        tabindex="0"
        className='tour-overview-children-date-of-birth__fill-in-with-fake-data'
        onclick={(e) => {
          children.forEach((child) => {
            child.dateOfBirth = generateDOB({
              departureStartDate: this.departure.startDate.date,
              ageFrom: this.departure.childAgeFrom,
              ageTo: this.departure.childAgeTo,
              dateFormat: this.config.dateFormat
            })
          })

          this.isDobUpdated = true
          this.partyPicker.partyChanged()

          e.preventDefault()
        }}
      >
        Fill in with fake data
      </a>
    )

    return (
      <div className="tour-overview-children-date-of-birth">
        {fillInFakeDataButton}
        <h1 className="tour-overview-children-date-of-birth__title">
          Child Dates of Birth:
        </h1>
        <div className="o-accent-box u-margin-bottom-large tour-overview-children-date-pickers">
          {datePickers}
        </div>
      </div>
    )
  }


  renderAdditionalCTA () {
    const isLargeDevice = responsiveRender('large', () => false, () => true)
    if (isLargeDevice) return

    const {extrasEnabled, showExtrasHyperlink} = this.config.features
    if (!extrasEnabled && showExtrasHyperlink) {
      return <button id="revealExtrasButton" type="button" class="btn-primary--reversed-colors -full-width" onclick={() => this.revealExtras()}>
        Add Extras
      </button>
    }

  }

  revealExtras () {
    this.config.features.extrasEnabled = true
    this.nextTab()
  }
}

